import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Building2, Loader2, ExternalLink, ArrowRightLeft, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface RelatedEntity {
  relatedEntityId: string | null;
  relatedEntityName: string | null;
  relatedAccountId: string | null;
  relatedAccountName: string | null;
  relatedContactId?: string | null;
  relatedContactName?: string | null;
  relatedEntityType: string | null;
  relationshipType: string;
  direction: "outgoing" | "incoming";
  appointmentDate: string | null;
  ceasedDate: string | null;
  ownershipPercentage: string | null;
  isActive: boolean;
}

interface RelatedEntitiesResponse {
  accountId: string;
  count: number;
  relationships: RelatedEntity[];
}

interface RelatedEntitiesPanelProps {
  accountId: string;
  entityType?: string;
  trustType?: string;
}

const RELATIONSHIP_CONFIG: Record<
  string,
  {
    label: string;
    outgoingLabel: string;
    incomingLabel: string;
    borderColor: string;
  }
> = {
  corporate_trustee: {
    label: "Corporate Trustee",
    outgoingLabel: "HAS trustee",
    incomingLabel: "IS trustee of",
    borderColor: "border-l-purple-500",
  },
  shareholder: {
    label: "Shareholder",
    outgoingLabel: "SHARES OWNED BY",
    incomingLabel: "OWNS shares in",
    borderColor: "border-l-blue-500",
  },
  beneficiary: {
    label: "Beneficiary",
    outgoingLabel: "HAS beneficiary",
    incomingLabel: "IS beneficiary of",
    borderColor: "border-l-green-500",
  },
  linked_bare_trust: {
    label: "Linked Bare Trust",
    outgoingLabel: "HAS bare trust",
    incomingLabel: "LINKED to SMSF",
    borderColor: "border-l-orange-500",
  },
  individual_trustee: {
    label: "Individual Trustee",
    outgoingLabel: "HAS trustee",
    incomingLabel: "IS trustee of",
    borderColor: "border-l-indigo-500",
  },
};

function getDirectionPill(relationshipType: string, direction: "outgoing" | "incoming"): string {
  const config = RELATIONSHIP_CONFIG[relationshipType];
  if (!config) return direction;
  return direction === "outgoing" ? config.outgoingLabel : config.incomingLabel;
}

function getDirectionVariant(direction: "outgoing" | "incoming"): "default" | "secondary" {
  return direction === "outgoing" ? "default" : "secondary";
}

export const RelatedEntitiesPanel: React.FC<RelatedEntitiesPanelProps> = ({
  accountId,
}) => {
  const [, navigate] = useLocation();

  const { data, isLoading } = useQuery<RelatedEntitiesResponse>({
    queryKey: ["/api/crm/accounts", accountId, "related-entities"],
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const relationships = data?.relationships ?? [];

  if (relationships.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="related-entities-empty">
        <ArrowRightLeft className="h-10 w-10 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">
          No structurally related entities were found for this account.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Add trustees, shareholders, or beneficiaries via the Structure tab.
        </p>
      </div>
    );
  }

  // Group by relationshipType
  const grouped = relationships.reduce<Record<string, RelatedEntity[]>>((acc, rel) => {
    if (!acc[rel.relationshipType]) {
      acc[rel.relationshipType] = [];
    }
    acc[rel.relationshipType].push(rel);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="related-entities-panel">
      {Object.entries(grouped).map(([type, rels]) => {
        const config = RELATIONSHIP_CONFIG[type] ?? {
          label: type.replace(/_/g, " "),
          borderColor: "border-l-gray-500",
        };

        return (
          <div key={type} className="border rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-muted-foreground">
                {config.label.toUpperCase()} ({rels.length})
              </h3>
            </div>
            <div className="space-y-2">
              {rels.map((rel) => (
                <Card
                  key={`${rel.relationshipType}-${rel.relatedEntityId ?? rel.relatedAccountId ?? rel.relatedContactId}-${rel.direction}`}
                  className={`border-l-4 ${config.borderColor}`}
                  data-testid={`row-related-entity-${rel.relatedEntityId ?? rel.relatedAccountId ?? rel.relatedContactId ?? "unknown"}`}
                >
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {rel.relatedEntityType === "contact" ? (
                            <User className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          )}
                          <button
                            onClick={() => {
                              if (rel.relatedEntityType === "contact" && rel.relatedContactId) {
                                navigate(`/contacts/${rel.relatedContactId}`);
                                return;
                              }
                              if (rel.relatedAccountId) {
                                navigate(`/accounts/${rel.relatedAccountId}`);
                              }
                            }}
                            className="font-medium text-sm text-primary hover:underline"
                            data-testid={`link-related-${rel.relatedEntityId ?? rel.relatedAccountId ?? rel.relatedContactId ?? "unknown"}`}
                          >
                            {rel.relatedEntityName || rel.relatedContactName || rel.relatedAccountName || "Unknown"}
                          </button>
                          <Badge
                            variant={getDirectionVariant(rel.direction)}
                            className="text-xs"
                            data-testid={`pill-direction-${rel.relatedEntityId ?? rel.relatedAccountId ?? rel.relatedContactId ?? "unknown"}`}
                          >
                            {getDirectionPill(rel.relationshipType, rel.direction)}
                          </Badge>
                          {!rel.isActive && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">
                              Ceased
                            </Badge>
                          )}
                          {rel.ownershipPercentage && (
                            <Badge variant="outline" className="text-xs">
                              {rel.ownershipPercentage}%
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {rel.relatedEntityType
                            ? rel.relatedEntityType.charAt(0).toUpperCase() +
                              rel.relatedEntityType.slice(1).replace(/_/g, " ")
                            : "Account"}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (rel.relatedEntityType === "contact" && rel.relatedContactId) {
                            navigate(`/contacts/${rel.relatedContactId}`);
                            return;
                          }
                          if (rel.relatedAccountId) {
                            navigate(`/accounts/${rel.relatedAccountId}`);
                          }
                        }}
                        title={rel.relatedEntityType === "contact" ? "View Contact" : "View Account"}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
